import React, { useEffect, useState, useCallback, useMemo } from 'react';
import StatCard from '../../components/stat-card/StatCard';
import Typography from '../../components/typography/Typography';
import DashboardOffersTable from '../../components/dashboard/OffersTable';
import DashboardRevShareOffersTable from '../../components/dashboard/RevShareOffersTable';
import staffApi, { DashboardStats, Offer } from '../../api/staff';
import { refShareApi, RefShareOffer, OfferStatus } from '../../api/services/refShareApi';
import { showPartnerSuccess, showError } from '../../utils/toast';
import { useTranslations } from '../../contexts/translation.context';
import { usePageBreadcrumbs } from '../../hooks/usePageBreadcrumbs';

const DashboardPage: React.FC = () => {
  const { t } = useTranslations();
  const [stats, setStats] = useState<DashboardStats>({});
  const [cplRevenue, setCplRevenue] = useState<number>(0);
  const [revShareRevenue, setRevShareRevenue] = useState<number>(0);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [revShareOffers, setRevShareOffers] = useState<RefShareOffer[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isLoadingOffers, setIsLoadingOffers] = useState(true);
  const [isLoadingRevShareOffers, setIsLoadingRevShareOffers] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Breadcrumbs
  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), isActive: true }
  ], [t]);

  usePageBreadcrumbs(breadcrumbs);

  // Вычисляем период для отображения (текущий месяц с 1 числа по сегодня)
  const dateRange = useMemo(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);

    const formatDate = (date: Date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    };

    return `${formatDate(firstDay)} - ${formatDate(now)}`;
  }, []);

  // Вычисляем даты для API запросов (формат YYYY-MM-DD)
  const apiDateRange = useMemo(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);

    const formatDateForApi = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      start_date: formatDateForApi(firstDay),
      end_date: formatDateForApi(now),
    };
  }, []);

  // Загрузка данных дашборда
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoadingStats(true);
        const [dashboardData, cplAmount, refShareAmount] = await Promise.all([
          staffApi.getDashboard(),
          staffApi.getCPLCurrentMonthAmount(apiDateRange),
          staffApi.getRefShareCurrentMonthAmount(apiDateRange)
        ]);
        setStats(dashboardData);
        setCplRevenue(cplAmount);
        setRevShareRevenue(refShareAmount);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Не удалось загрузить данные дашборда');
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchDashboardData();
  }, [apiDateRange]);

  // Загрузка последних 5 CPL офферов
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setIsLoadingOffers(true);
        const response = await staffApi.getOffers({
          page: 1,
          page_size: 5,
          sort: '-created_at' // Сортировка по дате создания (новые первыми)
        });
        setOffers(response.objects);
      } catch (err) {
        console.error('Error fetching CPL offers:', err);
        setError('Не удалось загрузить CPL офферы');
      } finally {
        setIsLoadingOffers(false);
      }
    };

    fetchOffers();
  }, []);

  // Загрузка последних 5 RevShare офферов
  useEffect(() => {
    const fetchRevShareOffers = async () => {
      try {
        setIsLoadingRevShareOffers(true);
        const response = await refShareApi.getOffers({
          page: 1,
          page_size: 5,
          sort_by: '-created_at' // Сортировка по дате создания (новые первыми)
        });
        setRevShareOffers(response.objects);
      } catch (err) {
        console.error('Error fetching RevShare offers:', err);
      } finally {
        setIsLoadingRevShareOffers(false);
      }
    };

    fetchRevShareOffers();
  }, []);

  // Обработчик изменения статуса CPL оффера
  const handleStatusChange = useCallback(async (offerId: string, status: 'active' | 'deactivated') => {
    try {
      await staffApi.updateOffer(offerId, { status });

      // Обновляем список офферов
      setOffers(prevOffers =>
        prevOffers.map(offer =>
          offer.id === offerId ? { ...offer, status } : offer
        )
      );

      showPartnerSuccess(
        status === 'active' ? 'Оффер успешно активирован' : 'Оффер успешно деактивирован'
      );
    } catch (err:any) {
      console.error('Error updating offer status:', err);
      showError(err?.response?.data?.detail || 'Не удалось изменить статус оффера');
    }
  }, []);

  const handleApproveOffer = useCallback(async (offerId: string) => {
    try {
      await staffApi.approveCPLOffer(offerId);

      // Обновляем список офферов
      setOffers(prevOffers =>
        prevOffers.map(offer =>
          offer.id === offerId ? { ...offer, status: 'active' } : offer
        )
      );

      showPartnerSuccess('Оффер успешно одобрен');
    } catch (err: any) {
      console.error('Error approving offer:', err);
      showError(err?.response?.data?.detail || 'Не удалось одобрить оффер');
    }
  }, []);

  const handleRejectOffer = useCallback(async (offerId: string) => {
    try {
      await staffApi.rejectCPLOffer(offerId);

      // Обновляем список офферов
      setOffers(prevOffers =>
        prevOffers.map(offer =>
          offer.id === offerId ? { ...offer, status: 'deactivated' } : offer
        )
      );

      showPartnerSuccess('Оффер успешно отклонен');
    } catch (err: any) {
      console.error('Error rejecting offer:', err);
      showError(err?.response?.data?.detail || 'Не удалось отклонить оффер');
    }
  }, []);

  // Обработчик изменения статуса RevShare оффера
  const handleRevShareStatusChange = useCallback(async (modelId: string, status: OfferStatus) => {
    try {
      await refShareApi.updateOfferStatus(modelId, status);

      // Обновляем список офферов
      setRevShareOffers(prevOffers =>
        prevOffers.map(offer => {
          const offerId = offer._id || offer.id;
          if (offerId === modelId) {
            return {
              ...offer,
              offer: offer.offer ? { ...offer.offer, status } : { commission: 0, status },
              status
            };
          }
          return offer;
        })
      );

      showPartnerSuccess(
        status === 'active' ? 'Оффер RevShare успешно активирован' : 'Оффер RevShare успешно деактивирован'
      );
    } catch (err: any) {
      console.error('Error updating RevShare offer status:', err);
      showError(err?.response?.data?.detail || 'Не удалось изменить статус оффера RevShare');
    }
  }, []);

  return (
    <div className="flex flex-col w-full">
      {/* Page Header */}
      <div className="px-[24px] py-[16px]">
        <Typography variant="h5" className="text-[#11181C]">
          Дашборд
        </Typography>
      </div>

      {/* Content */}
      <div className="px-[24px] pb-[24px]">
        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-[24px] mb-[24px]">
          {/* Column 1: Users */}
          <div className="flex flex-col gap-[16px]">
            <Typography variant="h6" className="text-[#11181C] text-[20px] font-medium">
              Пользователи
            </Typography>
            <div className="border border-zinc-100 rounded-[14px] overflow-hidden">
              <StatCard
                label="Активных криэйторов"
                value={isLoadingStats ? '...' : stats.active_creators || 0}
                className="rounded-none border-0 border-b"
              />
              <StatCard
                label="Активных партнёров"
                value={isLoadingStats ? '...' : stats.active_partners || 0}
                className="rounded-none border-0"
              />
            </div>
          </div>

          {/* Column 2: Revenue */}
          <div className="flex flex-col gap-[16px]">
            <Typography variant="h6" className="text-[#11181C] text-[20px] font-medium">
              Доход
            </Typography>
            <div className="border border-zinc-100 rounded-[14px] overflow-hidden">
              <StatCard
                label={
                  <div className="flex items-center gap-[4px]">
                    <span>CPL</span>
                    <span className="text-[14px] font-medium text-[#71717A] px-[6px] bg-[#F4F4F5] rounded-[6px]">
                      {dateRange}
                    </span>
                  </div>
                }
                value={isLoadingStats ? '...' : `$${cplRevenue?.toFixed(2) || '0.00'}`}
                className="rounded-none border-0 border-b"
              />
              <StatCard
                label={
                  <div className="flex items-center gap-[4px]">
                    <span>RevShare</span>
                    <span className="text-[14px] font-medium text-[#71717A] px-[6px] bg-[#F4F4F5] rounded-[6px]">
                      {dateRange}
                    </span>
                  </div>
                }
                value={isLoadingStats ? '...' : `$${revShareRevenue?.toFixed(2) || '0.00'}`}
                className="rounded-none border-0"
              />
            </div>
          </div>

          {/* Column 3: New Registrations */}
          <div className="flex flex-col gap-[16px]">
            <Typography variant="h6" className="text-[#11181C] text-[20px] font-medium">
              Новые регистрации
            </Typography>
            <div className="border border-zinc-100 rounded-[14px] overflow-hidden">
              <StatCard
                label={
                  <div className="flex items-center gap-[4px]">
                    <span>Партнёры</span>
                    <span className="text-[14px] font-medium text-[#71717A] px-[6px] bg-[#F4F4F5] rounded-[6px]">
                      за 7 дней
                    </span>
                  </div>
                }
                value={isLoadingStats ? '...' : stats.new_partners || 0}
                className="rounded-none border-0 border-b"
              />
              <StatCard
                label={
                  <div className="flex items-center gap-[4px]">
                    <span>Криэйторы</span>
                    <span className="text-[14px] font-medium text-[#71717A] px-[6px] bg-[#F4F4F5] rounded-[6px]">
                      за 7 дней
                    </span>
                  </div>
                }
                value={isLoadingStats ? '...' : stats.new_creators || 0}
                className="rounded-none border-0"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-[16px] p-[16px] bg-red-50 border border-red-200 rounded-[12px]">
            <Typography variant="body2" className="text-red-600">
              {error}
            </Typography>
          </div>
        )}

        {/* New CPL Offers Table */}
        <div className="mt-[32px]">
          <Typography variant="h6" className="text-[#11181C] text-[20px] font-medium mb-[16px]">
            Новые офферы CPL
          </Typography>
          <div className="min-h-[320px]">
            {isLoadingOffers ? (
              <div className="flex items-center justify-center h-[320px]">
                <Typography variant="body1" className="text-[#71717A]">
                  Загрузка офферов CPL...
                </Typography>
              </div>
            ) : offers && offers.length > 0 ? (
              <DashboardOffersTable
                offers={offers}
                onStatusChange={handleStatusChange}
                onApproveOffer={handleApproveOffer}
                onRejectOffer={handleRejectOffer}
              />
            ) : (
              <div className="flex items-center justify-center h-[320px]">
                <Typography variant="body1" className="text-[#71717A]">
                  Нет доступных CPL офферов
                </Typography>
              </div>
            )}
          </div>
        </div>

        {/* New RevShare Offers Table */}
        <div className="mt-[32px]">
          <Typography variant="h6" className="text-[#11181C] text-[20px] font-medium mb-[16px]">
            Новые офферы RevShare
          </Typography>
          <div className="min-h-[320px]">
            {isLoadingRevShareOffers ? (
              <div className="flex items-center justify-center h-[320px]">
                <Typography variant="body1" className="text-[#71717A]">
                  Загрузка офферов RevShare...
                </Typography>
              </div>
            ) : revShareOffers && revShareOffers.length > 0 ? (
              <DashboardRevShareOffersTable
                offers={revShareOffers}
                onStatusChange={handleRevShareStatusChange}
              />
            ) : (
              <div className="flex items-center justify-center h-[320px]">
                <Typography variant="body1" className="text-[#71717A]">
                  Нет доступных RevShare офферов
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
